import {createSlice} from '@reduxjs/toolkit';
import {
	GET_PROJECT_INDEPENDENCE_DETAILS,
	RESET_INDEPENDENCE,
	PATCH_INDEPENDENCE_USERS,
	SET_CANVAS_ENGAGEMENT_ID,
	SET_INDEPENDENCE_FILTER,
	POST_INDEPENDENCE_ENGAGEMENT_USERS,
	UPDATE_USER_ENGAGMENT_INVITED_FLAG
} from './ActionCreators';
import {canvasEngagementsAdapter} from './Adapters/CanvasEngagementAdapter';
import {teamMembersAdapter} from './Adapters/TeamMembersAdapter';
import {financialEngagementsAdapter} from './Adapters/FinancialEngagementsAdapter';
import {getIndependenceKey,currentResource} from '../../../../../common/utils/utils';
import {independenceKeyType} from '../../../../../common/utils/uiconstants';
import {filterAdapter} from './Adapters/FilterAdapter';

const initialState = {
	financialEngagements: financialEngagementsAdapter.getInitialState({}),
	canvasEngagements: canvasEngagementsAdapter.getInitialState({}),
	teamMembers: teamMembersAdapter.getInitialState({}),
	filter: {
		financialCodes: filterAdapter.getInitialState({})
	},
	dataLoaded: false,
	concurrencyToken: null,
	selectedCanvasEngagementId: null
};

export const independenceSlice = createSlice({
	name: 'independence',
	initialState,
	extraReducers: (builder) => {
		builder
			.addCase(GET_PROJECT_INDEPENDENCE_DETAILS, (state, action) => {
				if (action.error) {
					return state;
				}

				const financialEngagementsCollection = action.response.finEngs.map(
					(finEng) => {
						return {
							financialEngagementId: finEng.finEngCd,
							canvasEngagements: finEng.canvasEngs.map(
								(canvasEng) => finEng.finEngCd + '_' + canvasEng.canvasEngId
							)
						};
					}
				);

				const canvasEngagementsCollection = action.response.finEngs.flatMap(
					(finEng) => {
						return finEng.canvasEngs.map((canvasEng) => {
							return {
								canvasEngagementId: canvasEng.canvasEngId,
								id: finEng.finEngCd + '_' + canvasEng.canvasEngId,
								isIndependenceTemplateAvailable: canvasEng.IsIndTmpAvlbl,
								teamMembers: canvasEng.teamMembers.map((teamMember) => {
									return (
										finEng.finEngCd +
										'_' +
										canvasEng.canvasEngId +
										'_' +
										teamMember.userGui
									);
								})
							};
						});
					}
				);

				const teamMembersCollection = action.response.finEngs.flatMap(
					(finEng) => {
						return finEng.canvasEngs.flatMap((canvasEng) => {
							return canvasEng.teamMembers.map((teamMember) => {
								const localizedRankName = currentResource.userRanks.find(
									(r) => r.id === teamMember.rankCd
								)?.name ?? currentResource.labels.administrativeContractor;
								return {
									...teamMember,
									canvasEngId: canvasEng.canvasEngId,
									id:
										finEng.finEngCd +
										'_' +
										canvasEng.canvasEngId +
										'_' +
										teamMember.userGui,
									rankName:localizedRankName
								};
							});
						});
					}
				);

				financialEngagementsAdapter.setAll(
					state.financialEngagements,
					financialEngagementsCollection
				);

				canvasEngagementsAdapter.setAll(
					state.canvasEngagements,
					canvasEngagementsCollection
				);

				teamMembersAdapter.setAll(state.teamMembers, teamMembersCollection);

				state.dataLoaded = true;
				state.concurrencyToken = action.response?.concurrencyToken ?? null;
			})
			.addCase(RESET_INDEPENDENCE, (state, action) => {
				state.financialEngagements = initialState.financialEngagements;
				state.canvasEngagements = initialState.canvasEngagements;
				state.teamMembers = initialState.teamMembers;
				state.dataLoaded = initialState.dataLoaded;
				state.concurrencyToken = null;
			})
			.addCase(POST_INDEPENDENCE_ENGAGEMENT_USERS, (state, action) => {
				if (action.error) {
					return state;
				}
			})
			.addCase(PATCH_INDEPENDENCE_USERS, (state, action) => {
				if (action.error) {
					return state;
				}

				const response = action.response;

				const map = new Map();

				response.userInd.forEach((user) => {
					const key = `${response.canEngId}_${user.userGui}`;
					if (!map.has(key)) {
						map.set(key, user);
					}
				});

				const ids = state.teamMembers.ids.filter((id) => {
					const key = getIndependenceKey(
						id,
						independenceKeyType.EngagementTeamMember
					);
					if (map.has(key)) {
						return true;
					}
					return false;
				});

				for (const id in state.teamMembers.entities) {
					if (ids.includes(id)) {
						const key = getIndependenceKey(
							id,
							independenceKeyType.EngagementTeamMember
						);
						const teamMember = map.get(key);
						state.teamMembers.entities[id].userGui = teamMember.userGui;
						state.teamMembers.entities[id].indStatus = teamMember.indStatus;
						state.teamMembers.entities[id].indSource = teamMember.indSource;
						state.teamMembers.entities[id].rationale = teamMember.rationale;
						state.teamMembers.entities[id].indCompType = teamMember.indCompType;
						state.teamMembers.entities[id].modifyDate = teamMember.modifiedDate;
						state.teamMembers.entities[id].modifiedBy = teamMember.modifiedBy;
						state.teamMembers.entities[id].createdByUserName =
							teamMember.createdByUserName;
						state.teamMembers.entities[id].modifiedByUserName =
							teamMember.modifiedByUserName;
						state.teamMembers.entities[id].createDate = teamMember.createDate;
						state.teamMembers.entities[id].createdBy = teamMember.createdBy;
					}
				}

				state.concurrencyToken = action.response.concurrencyToken;
			})
			.addCase(SET_CANVAS_ENGAGEMENT_ID, (state, action) => {
				state.selectedCanvasEngagementId = Number(
					action.payload.canvasEngagementId
				);
			})
			.addCase(SET_INDEPENDENCE_FILTER, (state, action) => {
				if (Array.isArray(action.payload.initialFilters)) {
					filterAdapter.setAll(
						state.filter.financialCodes,
						action.payload.initialFilters
					);
				} else {
					const id = action.payload.id;

					if (state.filter.financialCodes.ids.includes(id)) {
						state.filter.financialCodes.entities[id].selected =
							action.payload.selected;
					}
				}
			})
			.addCase(UPDATE_USER_ENGAGMENT_INVITED_FLAG, (state, action) => {
				action.payload.map((userKey) => {
					for (const entityKey in state.teamMembers.entities) {
						const parts = entityKey.split('_');
						const shortKey = parts.slice(1).join('_');

						if (shortKey === userKey) {
							state.teamMembers.entities[
								entityKey
							].isUsrEngmntInviteSent = true;
						}
					}
				});
			});
	}
});
